<template>
  <TModal
    header="HTML Editor"
    xl
    header-low
    no-min-height
    id="tiptap-html-editor-modal"
    :inverse="inverse"
    @close="$emit('close')"
  >
    <template #dialog-body>
      <div id="tiptap-html-editor" class="h-[350px] relative"></div>
    </template>
    <template #dialog-footer>
      <div class="px-5 py-5 flex justify-end">
        <TButton success @click="onOkButtonClick()">Update</TButton>
      </div>
    </template>
  </TModal>
</template>
<script lang="ts" setup>
import IEditorConfig from "../../../types/IEditorConfig";
import xss from "xss";
// @ts-ignore
import cssfilter from "cssfilter";
// @ts-ignore
import * as diff from "diff";
import { useI18n } from "vue-i18n";
import getXssWhiteList from "./getXssWhiteList";
import {
  unescapeConditionalHtmlComments,
  getCopiedElement,
} from "@topol/utils";
import { TInput, TModal, TButton } from "@topol/twig-ui";
import { Editor } from "@tiptap/core";
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps<{
  tiptapEditor: Editor;
  mergeTags?: IEditorConfig["mergeTags"];
  inverse?: boolean;
}>();

const emits = defineEmits(["set-content", "close"]);

const { t } = useI18n();

const editor = ref<any>();
const beforeContent = ref("");
const markers = ref<number[]>([]);
const openingTags = ref<any[]>([]);
const openingTagIndex = ref<number>(0);
const isValid = ref(true);
// const isStyle = ref(false);
const unclosedIndex = ref(0);
const settingNewContent = ref(false);

const newContent = computed(() => {
  return props.tiptapEditor.getHTML();
});

const content = computed(() => {
  if (!props.tiptapEditor) return "";
  let html = props.tiptapEditor.getHTML();
  html = html.replace(
    /font-family:\s*&quot;([^&]+)&quot;/g,
    "font-family: '$1'"
  );
  return formatHTML(html);
});

function formatHTML(html: string): string {
  let formatted = "";
  let indent = 0;

  // Split na tagy a text
  const tokens = html.match(/<[^>]+>|[^<]+/g) || [];

  tokens.forEach((token) => {
    if (!token.trim()) return;

    // Uzavírací tag
    if (token.startsWith("</")) {
      indent--;
      formatted += "  ".repeat(Math.max(0, indent)) + token + "\n";
    }
    // Otevírací tag (ne self-closing)
    else if (
      token.startsWith("<") &&
      !token.endsWith("/>") &&
      !token.match(/<(br|img|hr|input|area|meta|link)/i)
    ) {
      formatted += "  ".repeat(indent) + token + "\n";
      indent++;
    }
    // Self-closing nebo void elementy
    else if (token.startsWith("<")) {
      formatted += "  ".repeat(indent) + token + "\n";
    }
    // Text content
    else {
      const trimmed = token.trim();
      if (trimmed) {
        formatted += "  ".repeat(indent) + trimmed + "\n";
      }
    }
  });

  return formatted;
}

const newCssWhiteList = cssfilter.whiteList;
for (const key in newCssWhiteList) {
  newCssWhiteList[key] = true;
}

const newWhiteList = getXssWhiteList();

function allowDataAttributes(
  _tag: string,
  name: string,
  value: string
): string | void {
  if (name.startsWith("data-")) {
    // @ts-expect-error
    return `${name}="${xss.escapeAttrValue(value)}"`;
  }
}

function fixHTML() {
  const sanitizedHTML = highlightInvalidHTML(
    editor.value.getValue(),
    editor.value
  );
  if (!isValid.value) {
    beforeContent.value = sanitizedHTML;
  }
}

function onOkButtonClick() {
  if (!isValid.value) {
    fixHTML();
  }
  const rawHTML = editor.value.getValue();
  emits("set-content", rawHTML);
  emits("close");
}

function clearAllMarkers(aceEditor: any) {
  markers.value.forEach((markerId) => {
    aceEditor.getSession().removeMarker(markerId);
  });
  markers.value = [];
}

function closeTag(tag: string) {
  let isClosing = false;
  for (let i = openingTags.value.length - 1; i >= 0; i--) {
    if (openingTags.value[i].tag === tag && !isClosing) {
      isClosing = true;
      openingTags.value.splice(i, 1);
      break;
    }
  }
  return isClosing;
}

function onTagRemoveUnusedClosingTags(tag: string, html: string, options: any) {
  if (options.isClosing && !closeTag(tag)) {
    return "";
  }

  if (!options.isClosing && !html.endsWith("/>") && tag !== "img") {
    openingTags.value.unshift({ tag: tag, index: openingTagIndex.value });
    openingTagIndex.value++;
  }
  //@ts-expect-error
  return xss.onTag(tag, html, options);
}

function mergetagRegexTest(str: string) {
  const placeReg = /^MERGE_TAG_\d+_PLACEHOLDER/;
  return placeReg.test(str);
}

function getMergetagsRegex() {
  if (!props.mergeTags) {
    return /$^/;
  }
  const syntax = props.mergeTags?.syntax || {
    start: "*|",
    end: "|*",
  };

  const merge_tags_syntax_start_escaped =
    "\\" + syntax.start.split("").join("\\");
  const merge_tags_syntax_end_escaped = "\\" + syntax.end.split("").join("\\");

  return new RegExp(
    `${merge_tags_syntax_start_escaped}[\\s\\S]*?${merge_tags_syntax_end_escaped}`,
    "g"
  );
}

function customSafeAttrValue(tag: string, name: string, value: string) {
  if (name === "style") {
    if (
      value.replaceAll(" ", "") ===
      cssfilter(value, {
        whiteList: newCssWhiteList,
      }).replaceAll(" ", "")
    ) {
      return value;
    } else
      return cssfilter(value, {
        whiteList: newCssWhiteList,
      });
  }

  if ((name === "href" || name === "src") && mergetagRegexTest(value)) {
    return value;
  }

  //@ts-expect-error
  return xss.safeAttrValue(tag, name, value);
}

// function noEscapeStyle(html: string) {
//   if (isStyle.value) {
//     return html;
//   }
//   //@ts-expect-error
//   return xss.escapeHtml(html);
// }

function removeUnclosedTags(tag: string, html: string, options: any) {
  if (!options.isClosing && !html.endsWith("/>")) {
    if (
      openingTags.value.find(
        (tagObject) =>
          tagObject.index === unclosedIndex.value && tagObject.tag === tag
      )
    ) {
      unclosedIndex.value++;
      return html.replace(">", " />");
    }
    unclosedIndex.value++;
  }
  // @ts-expect-error
  return xss.onTag(tag, html, options);
}

function getSanitizedHTMLWithoutUnusedClosingTags(html: string) {
  openingTags.value = [];
  openingTagIndex.value = 0;
  const options = {
    allowCommentTag: true,
    whiteList: newWhiteList,
    onTag: onTagRemoveUnusedClosingTags,
    onIgnoreTagAttr: allowDataAttributes,
    safeAttrValue: customSafeAttrValue,
    //@ts-expect-error
    escapeHtml: xss.escapeHtml,
  };
  return xss(html, options);
}

function getSanitizedHTMLWithoutUnclousedTags(html: string) {
  unclosedIndex.value = 0;
  const options = {
    allowCommentTag: true,
    whiteList: newWhiteList,
    onTag: removeUnclosedTags,
    onIgnoreTagAttr: allowDataAttributes,
    safeAttrValue: customSafeAttrValue,
    //@ts-expect-error
    escapeHtml: xss.escapeHtml,
  };
  return xss(html, options);
}

function highlightInvalidHTML(html: string, aceEditor: any) {
  isValid.value = true;
  clearAllMarkers(aceEditor);

  const replacementsMT: {
    placeholder: string;
    original: string;
  }[] = [];

  const sanitizedMT = html.replace(getMergetagsRegex(), (match) => {
    const placeholder = `MERGE_TAG_${replacementsMT.length}_PLACEHOLDER`;
    replacementsMT.push({ placeholder: placeholder, original: match });
    return placeholder;
  });

  const sanitizedHTML = getSanitizedHTMLWithoutUnusedClosingTags(sanitizedMT);
  let sanitizedHTMLWithComments =
    unescapeConditionalHtmlComments(sanitizedHTML);
  if (openingTags.value.length > 0) {
    const resanitizedHTML = getSanitizedHTMLWithoutUnclousedTags(sanitizedHTML);
    sanitizedHTMLWithComments =
      unescapeConditionalHtmlComments(resanitizedHTML);
  }

  if (!sanitizedHTMLWithComments) {
    return html;
  }

  replacementsMT.forEach((replacement) => {
    sanitizedHTMLWithComments = sanitizedHTMLWithComments.replace(
      replacement.placeholder,
      replacement.original
    );
  });

  if (html !== sanitizedHTMLWithComments) {
    isValid.value = false;
    const annotations: any[] = [];
    const sanitizedLines = sanitizedHTMLWithComments.split("\n");
    const differences = diff
      .diffChars(html, sanitizedHTMLWithComments)
      .filter((difference: any) => !difference.added);
    let difIndex = 0;
    let lineIndex = 0;
    let sanitizeLines = 0;
    let removedLines = 0;
    let annotationLineAdds = 0;
    differences.forEach((difference: any) => {
      if (!difference.count) return;
      const startLine = lineIndex;
      const startDifIndex = difIndex;
      const startSanitizeLines = sanitizeLines;
      const match = difference.value.match(/^\t*/);
      difIndex += difference.count;
      if (difference.value.includes("\n")) {
        const splitByLine = difference.value.split("\n");
        lineIndex += splitByLine.length - 1;
        difIndex = splitByLine[splitByLine.length - 1].length;
        if (difference.removed) {
          removedLines += splitByLine.length - 1;
        }
        sanitizeLines = lineIndex - removedLines;
      }
      const onlyNewLine = match && match[0] ? "\n" + match[0] : "\n";
      if (difference.removed && difference.value !== onlyNewLine) {
        const marker = aceEditor.getSession().addMarker(
          // @ts-expect-error
          new ace.Range(startLine, startDifIndex, lineIndex, difIndex),
          "myCustomMouseOverHighlight",
          "text"
        );
        markers.value.push(marker);
        if (startSanitizeLines !== annotationLineAdds || startLine === 0) {
          annotationLineAdds = startSanitizeLines;
          const sanizeText = match
            ? sanitizedLines[startSanitizeLines].replace(match[0], "")
            : sanitizedLines[startSanitizeLines];
          annotations.push({
            row: startLine,
            column: 0,
            text: t("errors.invalid_HTML_row") + "\n" + sanizeText,
            type: "error",
          });
        }
      }
    });

    setTimeout(() => {
      aceEditor.getSession().setAnnotations(annotations);
    }, 10);
  } else {
    aceEditor.getSession().clearAnnotations();
  }

  return sanitizedHTMLWithComments;
}

watch(newContent, (value) => {
  if (beforeContent.value !== value) {
    settingNewContent.value = true;
    editor.value.setValue(value, 1);
  }
});

onMounted(() => {
  //@ts-expect-error
  // eslint-disable-next-line no-undef
  editor.value = ace.edit("tiptap-html-editor");
  editor.value.setValue(content.value, 1);
  editor.value.setTheme("ace/theme/chrome");

  // mode-xxx.js or theme-xxx.jsがある場合のみ有効
  editor.value.getSession().setMode(`ace/mode/html`);

  //Remove unreasonable error that HTML doesnt start with DOCTYPE
  //This is unfortunately effecting all of the syntax checking
  //But there is no point in using the error checking when the DOCTYPE error is still in place
  editor.value.getSession().setUseWorker(false);

  editor.value.$blockScrolling = Infinity;
  highlightInvalidHTML(editor.value.getValue(), editor.value);

  editor.value.on("change", () => {
    highlightInvalidHTML(editor.value.getValue(), editor.value);
    beforeContent.value = editor.value.getValue();
    if (settingNewContent.value) {
      settingNewContent.value = false;
      return;
    }
    // emits("change-content", editor.value.getValue());
  });
  editor.value.onPaste = function (text: string, event?: ClipboardEvent) {
    if (getCopiedElement(text)) {
      return;
    }
    const e = { text: text, event: event };
    editor.value.commands.exec("paste", editor.value, e);
  };
});

// onBeforeUnmount(() => {
//   if (
//     editor.value &&
//     JSON.stringify(blockIndexes.value) !==
//       JSON.stringify(mjmlStore.selectedBlock)
//   ) {
//     fixHTML();
//   }
// });
</script>

<style lang="scss">
.myCustomMouseOverHighlight {
  border-bottom: 1px solid red;
  position: absolute;
  pointer-events: auto;
  z-index: 3;
}
</style>
